// src/features/calendar/components/JumpToDateDialogV2.tsx
import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { format, subDays, differenceInCalendarDays } from 'date-fns';
import { DateRange, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// react-date-range men-hardcode `.rdrMonth { width: 27.667em }` (~443px pada
// font-size 16px) — lebih lebar dari HAMPIR SEMUA layar HP, itu sebabnya modal
// ini selalu "kepotong" di sisi kiri (seluruh isi modal ikut melebar mengikuti
// kalender di dalamnya). Override di sini SENGAJA di-scope lewat wrapper
// (bukan global index.css) supaya tidak mempengaruhi pemakaian react-date-range
// lain (mis. RoomMaintenanceFilterComponent, JumpToDateRangeControl versi
// desktop) yang justru butuh lebar aslinya.
const MobileDateRangeWrapper = styled.div`
  width: 100%;
  .rdrCalendarWrapper {
    width: 100%;
    max-width: 100%;
    font-size: 14px;
  }
  .rdrMonth {
    width: 100%;
  }
`;

interface JumpToDateForm {
    start_date: string;
    end_date: string;
}

interface JumpToDateDialogV2Props {
    show: boolean;
    defStartDate: string;
    defEndDate: string;
    onClose: () => void;
    onChangeDate: (data: JumpToDateForm) => void;
}

const MIN_DAYS = 21;
const MAX_DAYS = 63;

/**
 * Versi mobile dari "Jump to Date" (dipakai lewat dropdown "Options" di
 * CalendarPage.tsx saat layar <=768px). Sebelumnya: input teks yang harus
 * di-tap dulu untuk membuka kalender ABSOLUTE-positioned di dalam modal, lalu
 * tap tombol "Apply" terpisah — gampang rusak di layar sempit (kalender bisa
 * lebih lebar dari modal/viewport) dan makan 3 tap. Sekarang: kalender
 * langsung tampil di dalam modal (block biasa, bukan absolute overlay — jadi
 * tidak bisa "kepotong" keluar layar), 1 bulan vertical (paling gampang dipakai
 * jari di HP), dan auto-apply + auto-close begitu tanggal KEDUA dipilih (sama
 * seperti versi desktop JumpToDateRangeControl) — tidak perlu tap "Apply" lagi.
 * Rentang di luar 21-63 hari di-klem otomatis (bukan ditolak lewat pesan error).
 */
const JumpToDateDialogV2: React.FC<JumpToDateDialogV2Props> = ({
    show,
    defStartDate,
    defEndDate,
    onClose,
    onChangeDate,
}) => {
    const [dateRange, setDateRange] = useState(() => ({
        startDate: new Date(defStartDate),
        endDate: new Date(defEndDate),
    }));

    // Reset preview ke rentang aktif setiap kali modal dibuka.
    useEffect(() => {
        if (show) {
            setDateRange({
                startDate: new Date(defStartDate),
                endDate: new Date(defEndDate),
            });
        }
    }, [show, defStartDate, defEndDate]);

    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (!selection.startDate || !selection.endDate) return;

        const isSameDay = selection.startDate.getTime() === selection.endDate.getTime();

        if (isSameDay) {
            // Klik pertama (pilih start baru) — cuma update preview, belum selesai.
            setDateRange({ startDate: selection.startDate, endDate: selection.endDate });
            return;
        }

        // startDate !== endDate = klik kedua sudah terjadi, rentang lengkap.
        let s = selection.startDate;
        const e = selection.endDate;
        const dayCount = differenceInCalendarDays(e, s) + 1;

        if (dayCount > MAX_DAYS) {
            s = subDays(e, MAX_DAYS - 1);
        } else if (dayCount < MIN_DAYS) {
            s = subDays(e, MIN_DAYS - 1);
        }

        onChangeDate({
            start_date: format(s, 'yyyy-MM-dd'),
            end_date: format(e, 'yyyy-MM-dd'),
        });
        onClose();
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Jump to Date</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0" style={{ overflowX: 'hidden' }}>
                <MobileDateRangeWrapper>
                    <DateRange
                        onChange={handleDateChange}
                        moveRangeOnFirstSelection={false}
                        ranges={[{ ...dateRange, key: 'selection' }]}
                        months={1}
                        direction="vertical"
                    />
                </MobileDateRangeWrapper>
            </Modal.Body>
        </Modal>
    );
};

export default JumpToDateDialogV2;
